import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useProject } from "../contexts/ProjectContext";
import { useAuth } from "../contexts/AuthContext";
import { useUsage } from "../hooks/useUsage";
import SubscriptionModal from "./SubscriptionModal";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ArrowLeft, Save, Settings as SettingsIcon, Eye, EyeOff, ExternalLink, HardDrive, Shield, Database, Trash2, AlertTriangle } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { mockModels } from "../mock";

const Settings = () => {
  const { settings, systemPrompts, updateSettings, encryptionSupported } = useProject();
  const { user } = useAuth(); // Get user to check admin status
  const { usage, refreshUsage } = useUsage();
  const { toast } = useToast();
  
  const [localSettings, setLocalSettings] = useState({
    openrouter_api_key: "",
    selected_model: "",
    default_system_prompt: ""
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveButtonState, setSaveButtonState] = useState("idle"); // idle, saving, saved
  
  // Cleanup feature state
  const [cleanupDays, setCleanupDays] = useState({
    projects: "",
    prompts: ""
  });
  const [projectsPreview, setProjectsPreview] = useState(null);
  const [promptsPreview, setPromptsPreview] = useState(null);
  const [showProjectsConfirm, setShowProjectsConfirm] = useState(false);
  const [showPromptsConfirm, setShowPromptsConfirm] = useState(false);
  const [projectsButtonState, setProjectsButtonState] = useState("idle"); // idle, previewing, cleaning, cleaned
  const [promptsButtonState, setPromptsButtonState] = useState("idle"); // idle, previewing, cleaning, cleaned
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [unlockCode, setUnlockCode] = useState("");

  useEffect(() => {
    setLocalSettings({
      openrouter_api_key: settings.openrouter_api_key || "",
      selected_model: settings.selected_model || "anthropic/claude-3-haiku",
      default_system_prompt: settings.default_system_prompt || null
    });
  }, [settings]);

  useEffect(() => {
    const hasChanges = 
      localSettings.openrouter_api_key !== (settings.openrouter_api_key || "") ||
      localSettings.selected_model !== (settings.selected_model || "anthropic/claude-3-haiku") ||
      localSettings.default_system_prompt !== (settings.default_system_prompt || null);
    setHasUnsavedChanges(hasChanges);
    
    // Reset button state when user starts making changes after a successful save
    if (hasChanges && saveButtonState === "saved") {
      setSaveButtonState("idle");
    }
  }, [localSettings, settings, saveButtonState]);

  const handleSave = async () => {
    setSaveButtonState("saving");
    try {
      await updateSettings(localSettings);
      setSaveButtonState("saved");
      
      // Reset button state after showing success
      setTimeout(() => {
        setSaveButtonState("idle");
      }, 2000);
      
      toast({
        title: "Settings Saved",
        description: "Your settings have been updated successfully"
      });
    } catch (error) {
      setSaveButtonState("idle");
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive"
      });
    }
  };

  const handleReset = () => {
    if (hasUnsavedChanges && window.confirm("Are you sure you want to discard your changes?")) {
      setLocalSettings({
        openrouter_api_key: settings.openrouter_api_key || "",
        selected_model: settings.selected_model || "anthropic/claude-3-haiku",
        default_system_prompt: settings.default_system_prompt || null
      });
    }
  };

  const selectedModelInfo = mockModels.find(m => m.id === localSettings.selected_model);

  const handleUnlock = async () => {
    try {
      const token = localStorage.getItem('itsmyvibe_token');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please log in again to continue",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/unlock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: unlockCode })
      });

      if (response.ok) {
        toast({
          title: "Success!",
          description: "You have unlocked unlimited access.",
        });
        refreshUsage();
        setUnlockCode("");
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Invalid unlock code.");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Cleanup functions
  const handleProjectsPreview = async () => {
    setProjectsButtonState("previewing");
    try {
      const projectDays = cleanupDays.projects ? parseInt(cleanupDays.projects) : null;
      
      if (projectDays === null || projectDays < 0) {
        toast({
          title: "Error",
          description: "Please enter a valid number of days for projects",
          variant: "destructive"
        });
        setProjectsButtonState("idle");
        return;
      }

      const token = localStorage.getItem('itsmyvibe_token');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please log in again to continue",
          variant: "destructive"
        });
        setProjectsButtonState("idle");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cleanup/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          delete_projects_older_than_days: projectDays
        })
      });

      if (response.status === 401) {
        toast({
          title: "Session Expired",
          description: "Please log in again to continue",
          variant: "destructive"
        });
        setProjectsButtonState("idle");
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setProjectsPreview(data);
        setShowProjectsConfirm(true);
        setProjectsButtonState("idle");
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      setProjectsButtonState("idle");
      console.error('Projects cleanup preview error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to get projects cleanup preview",
        variant: "destructive"
      });
    }
  };

  const handlePromptsPreview = async () => {
    setPromptsButtonState("previewing");
    try {
      const promptDays = cleanupDays.prompts ? parseInt(cleanupDays.prompts) : null;
      
      if (promptDays === null || promptDays < 0) {
        toast({
          title: "Error",
          description: "Please enter a valid number of days for prompts",
          variant: "destructive"
        });
        setPromptsButtonState("idle");
        return;
      }

      const token = localStorage.getItem('itsmyvibe_token');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please log in again to continue",
          variant: "destructive"
        });
        setPromptsButtonState("idle");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cleanup/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          delete_prompts_older_than_days: promptDays
        })
      });

      if (response.status === 401) {
        toast({
          title: "Session Expired",
          description: "Please log in again to continue",
          variant: "destructive"
        });
        setPromptsButtonState("idle");
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setPromptsPreview(data);
        setShowPromptsConfirm(true);
        setPromptsButtonState("idle");
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      setPromptsButtonState("idle");
      console.error('Prompts cleanup preview error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to get prompts cleanup preview",
        variant: "destructive"
      });
    }
  };

  const handleProjectsConfirm = async () => {
    setProjectsButtonState("cleaning");
    setShowProjectsConfirm(false);
    
    try {
      const projectDays = cleanupDays.projects ? parseInt(cleanupDays.projects) : null;

      const token = localStorage.getItem('itsmyvibe_token');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please log in again to continue",
          variant: "destructive"
        });
        setProjectsButtonState("idle");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cleanup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          delete_projects_older_than_days: projectDays
        })
      });

      if (response.status === 401) {
        toast({
          title: "Session Expired",
          description: "Please log in again to continue",
          variant: "destructive"
        });
        setProjectsButtonState("idle");
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setProjectsButtonState("cleaned");
        
        // Reset cleanup form
        setCleanupDays({ ...cleanupDays, projects: "" });
        setProjectsPreview(null);
        
        // Reset button state after showing success
        setTimeout(() => {
          setProjectsButtonState("idle");
        }, 2000);
        
        toast({
          title: "Projects Cleanup Complete",
          description: data.message
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      setProjectsButtonState("idle");
      console.error('Projects cleanup execution error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to perform projects cleanup",
        variant: "destructive"
      });
    }
  };

  const handlePromptsConfirm = async () => {
    setPromptsButtonState("cleaning");
    setShowPromptsConfirm(false);
    
    try {
      const promptDays = cleanupDays.prompts ? parseInt(cleanupDays.prompts) : null;

      const token = localStorage.getItem('itsmyvibe_token');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please log in again to continue",
          variant: "destructive"
        });
        setPromptsButtonState("idle");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cleanup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          delete_prompts_older_than_days: promptDays
        })
      });

      if (response.status === 401) {
        toast({
          title: "Session Expired",
          description: "Please log in again to continue",
          variant: "destructive"
        });
        setPromptsButtonState("idle");
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setPromptsButtonState("cleaned");
        
        // Reset cleanup form
        setCleanupDays({ ...cleanupDays, prompts: "" });
        setPromptsPreview(null);
        
        // Reset button state after showing success
        setTimeout(() => {
          setPromptsButtonState("idle");
        }, 2000);
        
        toast({
          title: "Prompts Cleanup Complete",
          description: data.message
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      setPromptsButtonState("idle");
      console.error('Prompts cleanup execution error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to perform prompts cleanup",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <SettingsIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-800">Settings</h1>
                  <p className="text-sm text-slate-500">Configure API, security, and preferences</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {hasUnsavedChanges && (
                <Button variant="outline" size="sm" onClick={handleReset}>
                  Reset
                </Button>
              )}
              <Button 
                onClick={handleSave} 
                disabled={!hasUnsavedChanges || saveButtonState !== "idle"}
                className={saveButtonState === "saved" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"}
              >
                <Save className="w-4 h-4 mr-2" />
                {saveButtonState === "saving" && "Saving..."}
                {saveButtonState === "saved" && "Saved!"}
                {saveButtonState === "idle" && "Save Settings"}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <Tabs defaultValue="account" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="account">Account</TabsTrigger>
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
            <TabsTrigger value="security">
              <Shield className="w-4 h-4 mr-2" />
              Security & Privacy
            </TabsTrigger>
            <TabsTrigger value="cleanup">
              <Trash2 className="w-4 h-4 mr-2" />
              Data Management
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="account" className="space-y-8">
            {/* Usage Overview */}
            <Card>
              <CardHeader>
                <CardTitle>Usage Overview</CardTitle>
                <CardDescription>
                  Your current usage and subscription status
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Projects</span>
                      <Badge variant={usage.subscription_status === 'free' ? 'secondary' : 'outline'}>
                        {usage.subscription_status === 'free' ? 'Free Plan' : 'Pro Plan'}
                      </Badge>
                    </div>
                    <div className="text-2xl font-bold">
                      {usage.projects_count}/
                      {usage.subscription_status === 'free' ? '10' : '∞'}
                    </div>
                    <div className="text-xs text-slate-500">projects created</div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">AI Improvements</span>
                      <Badge variant={usage.subscription_status === 'free' ? 'secondary' : 'outline'}>
                        {usage.subscription_status === 'free' ? 'Free Plan' : 'Pro Plan'}
                      </Badge>
                    </div>
                    <div className="text-2xl font-bold">
                      {usage.ai_improvements_used}/
                      {usage.subscription_status === 'free' ? '20' : '∞'}
                    </div>
                    <div className="text-xs text-slate-500">improvements used</div>
                  </div>
                </div>
                
                {usage.subscription_status === 'free' && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg">
                    <h4 className="font-medium text-violet-900 mb-2">Upgrade to Pro</h4>
                    <p className="text-sm text-violet-700 mb-3">
                      Get unlimited projects and AI improvements for just $5/month
                    </p>
                    <Button 
                      className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                      onClick={() => setShowSubscriptionModal(true)}
                    >
                      Upgrade Now
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Unlock Code */}
            <Card>
              <CardHeader>
                <CardTitle>Unlock Code</CardTitle>
                <CardDescription>
                  Enter a special code to unlock unlimited access.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex space-x-2">
                  <Input
                    id="unlock-code"
                    placeholder="Enter unlock code"
                    value={unlockCode}
                    onChange={(e) => setUnlockCode(e.target.value)}
                  />
                  <Button onClick={handleUnlock}>Unlock</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="preferences" className="space-y-8">
            {/* Default Preferences - moved before OpenRouter API Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Default Preferences</CardTitle>
                <CardDescription>
                  Set your default preferences for AI improvement
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="default-system-prompt">Default System Prompt</Label>
                  <Select 
                    value={localSettings.default_system_prompt || ""} 
                    onValueChange={(value) => setLocalSettings({ ...localSettings, default_system_prompt: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select default system prompt" />
                    </SelectTrigger>
                    <SelectContent>
                      {systemPrompts.map((sp) => (
                        <SelectItem key={sp.id} value={sp.id}>
                          <div className="flex items-center">
                            <span>{sp.name}</span>
                            {sp.is_default && (
                              <Badge variant="secondary" className="ml-2 text-xs">
                                Default
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    This system prompt will be pre-selected when improving prompts
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* OpenRouter API Configuration - only for admin users */}
            {user?.is_admin && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    OpenRouter API Configuration
                    <a 
                      href="https://openrouter.ai" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-violet-600 hover:text-violet-700 flex items-center"
                    >
                      Visit OpenRouter <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </CardTitle>
                  <CardDescription>
                    Configure your OpenRouter API key to enable AI prompt improvement (Admin only)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="api-key">API Key</Label>
                    <div className="relative">
                      <Input
                        id="api-key"
                        type={showApiKey ? "text" : "password"}
                        placeholder="sk-or-v1-..."
                        value={localSettings.openrouter_api_key}
                        onChange={(e) => setLocalSettings({ ...localSettings, openrouter_api_key: e.target.value })}
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Get your API key from{" "}
                      <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">
                        OpenRouter Dashboard
                      </a>
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="model">Preferred Model</Label>
                    <Select value={localSettings.selected_model} onValueChange={(value) => setLocalSettings({ ...localSettings, selected_model: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a model" />
                      </SelectTrigger>
                      <SelectContent>
                        {mockModels.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            <div className="flex items-center justify-between w-full">
                              <div className="flex flex-col">
                                <span className="font-medium">{model.name}</span>
                                <span className="text-xs text-slate-500">{model.provider} • {model.pricing}</span>
                              </div>
                              {model.free && (
                                <Badge variant="secondary" className="ml-2 text-xs">
                                  Free
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedModelInfo && (
                      <div className="mt-2 p-3 bg-slate-50 rounded-lg text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{selectedModelInfo.name}</span>
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline" className="text-xs">
                              {selectedModelInfo.provider}
                            </Badge>
                            {selectedModelInfo.free && (
                              <Badge variant="secondary" className="text-xs">
                                Free
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-slate-600 mt-1">Pricing: {selectedModelInfo.pricing}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Status */}
            <Card>
              <CardHeader>
                <CardTitle>Status</CardTitle>
                <CardDescription>Current configuration status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {user?.is_admin && (
                    <>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm font-medium">API Key Status</span>
                        <Badge variant={localSettings.openrouter_api_key ? "default" : "secondary"}>
                          {localSettings.openrouter_api_key ? "Configured" : "Not Set"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm font-medium">Selected Model</span>
                        <span className="text-sm text-slate-600">
                          {selectedModelInfo?.name || "None"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm font-medium">AI Improvement</span>
                        <Badge variant={localSettings.openrouter_api_key ? "default" : "secondary"}>
                          {localSettings.openrouter_api_key ? "Available" : "Requires API Key"}
                        </Badge>
                      </div>
                    </>
                  )}
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium">Data Storage</span>
                    <Badge variant="default">Encrypted Database</Badge>
                  </div>
                  {user?.is_admin && (
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm font-medium">Admin Access</span>
                      <Badge variant="default">Enabled</Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="security">
            <div className="space-y-6">
              {/* Encryption Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-green-500" />
                    End-to-End Encryption
                  </CardTitle>
                  <CardDescription>
                    Your data is encrypted before being sent to our servers
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium">Encryption Support</span>
                    <Badge variant={encryptionSupported ? "default" : "destructive"}>
                      {encryptionSupported ? "Enabled" : "Not Supported"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium">Algorithm</span>
                    <span className="text-sm text-slate-600">AES-256-GCM</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium">Key Storage</span>
                    <span className="text-sm text-slate-600">Browser Only</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium">Server Access</span>
                    <Badge variant="outline" className="text-green-700 border-green-300">
                      Cannot Read Data
                    </Badge>
                  </div>

                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-2">🔒 Privacy Protection</h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>• All prompts and projects are encrypted in your browser</li>
                      <li>• Only encrypted data is sent to our servers</li>
                      <li>• Your encryption key never leaves your device</li>
                      <li>• Even we cannot read your prompts or project names</li>
                      <li>• Data persists across browser sessions securely</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Data Persistence */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Database className="w-5 h-5 mr-2 text-blue-500" />
                    Data Persistence
                  </CardTitle>
                  <CardDescription>
                    Your data is safely stored and synchronized
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm font-medium">Storage Type</span>
                      <Badge variant="default">Encrypted Database</Badge>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm font-medium">Data Backup</span>
                      <Badge variant="default">Automatic</Badge>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm font-medium">Cache Clearing</span>
                      <Badge variant="default">Data Preserved</Badge>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm font-medium">Cross-Device Sync</span>
                      <Badge variant="secondary">Coming Soon</Badge>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">💾 Persistent Storage</h4>
                    <p className="text-sm text-blue-700">
                      Unlike localStorage, your data is now safely stored in an encrypted database. 
                      Clearing your browser cache will no longer delete your prompts and projects.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="cleanup">
            <div className="space-y-6">
              {/* Data Cleanup */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Trash2 className="w-5 h-5 mr-2 text-red-500" />
                    Data Cleanup
                  </CardTitle>
                  <CardDescription>
                    Clean up old projects and prompts to manage your storage
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <Label htmlFor="cleanup-projects">Delete projects older than (days)</Label>
                      <Input
                        id="cleanup-projects"
                        type="number"
                        min="0"
                        placeholder="e.g., 30 (use 0 for testing)"
                        value={cleanupDays.projects}
                        onChange={(e) => setCleanupDays({ ...cleanupDays, projects: e.target.value })}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Projects and all their prompts will be permanently deleted
                      </p>
                      <Button 
                        onClick={handleProjectsPreview}
                        disabled={!cleanupDays.projects || projectsButtonState !== "idle"}
                        variant="outline"
                        size="sm"
                        className={`w-full ${projectsButtonState === "cleaned" ? "bg-green-600 hover:bg-green-700 text-white border-green-600" : ""}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {projectsButtonState === "previewing" && "Getting Preview..."}
                        {projectsButtonState === "cleaning" && "Cleaning Projects..."}
                        {projectsButtonState === "cleaned" && "Projects Cleaned!"}
                        {projectsButtonState === "idle" && "Preview Project Cleanup"}
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      <Label htmlFor="cleanup-prompts">Delete prompts older than (days)</Label>
                      <Input
                        id="cleanup-prompts"
                        type="number"
                        min="0"
                        placeholder="e.g., 60 (use 0 for testing)"
                        value={cleanupDays.prompts}
                        onChange={(e) => setCleanupDays({ ...cleanupDays, prompts: e.target.value })}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Individual prompts will be deleted (projects remain)
                      </p>
                      <Button 
                        onClick={handlePromptsPreview}
                        disabled={!cleanupDays.prompts || promptsButtonState !== "idle"}
                        variant="outline"
                        size="sm"
                        className={`w-full ${promptsButtonState === "cleaned" ? "bg-green-600 hover:bg-green-700 text-white border-green-600" : ""}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {promptsButtonState === "previewing" && "Getting Preview..."}
                        {promptsButtonState === "cleaning" && "Cleaning Prompts..."}
                        {promptsButtonState === "cleaned" && "Prompts Cleaned!"}
                        {promptsButtonState === "idle" && "Preview Prompt Cleanup"}
                      </Button>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-800 mb-1">⚠️ Warning</h4>
                        <ul className="text-sm text-amber-700 space-y-1">
                          <li>• This action cannot be undone</li>
                          <li>• Deleted projects will remove all their prompts automatically</li>
                          <li>• You'll see a preview before any data is actually deleted</li>
                          <li>• Only your data will be affected (user isolation is maintained)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Storage Statistics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Database className="w-5 h-5 mr-2 text-blue-500" />
                    Storage Information
                  </CardTitle>
                  <CardDescription>
                    Overview of your current data usage
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm font-medium">Data Storage</span>
                      <Badge variant="default">Encrypted Database</Badge>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm font-medium">Data Retention</span>
                      <span className="text-sm text-slate-600">User Controlled</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm font-medium">Backup Status</span>
                      <Badge variant="default">Automatic</Badge>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">💾 Storage Benefits</h4>
                    <p className="text-sm text-blue-700">
                      Your data is stored in an encrypted database with automatic backups. 
                      Use the cleanup feature above to manage your storage and keep only the data you need.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Projects Cleanup Confirmation Dialog */}
        {showProjectsConfirm && projectsPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500 mr-2" />
                <h3 className="text-lg font-semibold">Confirm Projects Cleanup</h3>
              </div>
              
              <div className="mb-6 space-y-2">
                <p className="text-sm text-slate-600">This cleanup will permanently delete:</p>
                
                {projectsPreview.projects_to_delete > 0 && (
                  <div className="bg-red-50 p-3 rounded">
                    <p className="text-sm font-medium text-red-800">
                      {projectsPreview.projects_to_delete} projects
                    </p>
                    {projectsPreview.prompts_to_delete_with_projects > 0 && (
                      <p className="text-xs text-red-600">
                        + {projectsPreview.prompts_to_delete_with_projects} prompts (within those projects)
                      </p>
                    )}
                  </div>
                )}
                
                {projectsPreview.projects_to_delete === 0 && (
                  <div className="bg-green-50 p-3 rounded">
                    <p className="text-sm font-medium text-green-800">
                      No projects to delete with the specified criteria
                    </p>
                  </div>
                )}

                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ Projects older than {cleanupDays.projects} day{cleanupDays.projects === '1' ? '' : 's'} will be permanently deleted
                </p>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowProjectsConfirm(false);
                    setProjectsPreview(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleProjectsConfirm}
                  variant="destructive"
                  className="flex-1"
                  disabled={projectsPreview.projects_to_delete === 0}
                >
                  Delete Projects
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Prompts Cleanup Confirmation Dialog */}
        {showPromptsConfirm && promptsPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-orange-500 mr-2" />
                <h3 className="text-lg font-semibold">Confirm Prompts Cleanup</h3>
              </div>
              
              <div className="mb-6 space-y-2">
                <p className="text-sm text-slate-600">This cleanup will permanently delete:</p>
                
                {promptsPreview.prompts_to_delete_individually > 0 && (
                  <div className="bg-orange-50 p-3 rounded">
                    <p className="text-sm font-medium text-orange-800">
                      {promptsPreview.prompts_to_delete_individually} individual prompts
                    </p>
                    <p className="text-xs text-orange-600">
                      (Projects will remain unchanged)
                    </p>
                  </div>
                )}
                
                {promptsPreview.prompts_to_delete_individually === 0 && (
                  <div className="bg-green-50 p-3 rounded">
                    <p className="text-sm font-medium text-green-800">
                      No prompts to delete with the specified criteria
                    </p>
                  </div>
                )}

                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ Prompts older than {cleanupDays.prompts} day{cleanupDays.prompts === '1' ? '' : 's'} will be permanently deleted
                </p>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPromptsConfirm(false);
                    setPromptsPreview(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePromptsConfirm}
                  variant="destructive"
                  className="flex-1"
                  disabled={promptsPreview.prompts_to_delete_individually === 0}
                >
                  Delete Prompts
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        currentUsage={usage}
      />
    </div>
  );
};

export default Settings;